import parse from 'date-fns/parse';
import getHours from 'date-fns/getHours';
import getMinutes from 'date-fns/getMinutes';
import timeZoneData from './timezones.json';

export const defaultTimeSlot = [
  {
    day: 0,
    to: '',
    from: '',
    valid: false,
  },
  {
    day: 1,
    to: '',
    from: '',
    valid: false,
  },
  {
    day: 2,
    to: '',
    from: '',
    valid: false,
  },
  {
    day: 3,
    to: '',
    from: '',
    valid: false,
  },
  {
    day: 4,
    to: '',
    from: '',
    valid: false,
  },
  {
    day: 5,
    to: '',
    from: '',
    valid: false,
  },
  {
    day: 6,
    to: '',
    from: '',
    valid: false,
  },
];

export const generateTimeSlots = (step = 15) => {
  /*
    Generates a list of 24-hour time strings for one day. Each new string
     will be generated by adding `step` minutes to the previous one.
    The list is generated by starting with a random day and adding step minutes till end of the same day.
    Always includes 23:59 as the final slot to complete the day.
  */
  const date = new Date(1970, 1, 1);
  const slots = [];
  while (date.getDate() === 1) {
    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    slots.push(`${hour}:${minute}`);
    date.setMinutes(date.getMinutes() + step);
  }

  const lastSlot = '23:59';
  if (!slots.includes(lastSlot)) {
    slots.push(lastSlot);
  }

  return slots;
};

export const getTime = (hour, minute) => {
  const parsedHour = String(hour).padStart(2, '0');
  const parsedMinute = String(minute).padStart(2, '0');
  return `${parsedHour}:${parsedMinute}`;
};

export const timeSlotParse = timeSlots => {
  return timeSlots.map(slot => {
    const {
      day_of_week: day,
      open_hour: openHour,
      open_minutes: openMinutes,
      close_hour: closeHour,
      close_minutes: closeMinutes,
      closed_all_day: closedAllDay,
      open_all_day: openAllDay,
    } = slot;
    const from = closedAllDay ? '' : getTime(openHour, openMinutes);
    const to = closedAllDay ? '' : getTime(closeHour, closeMinutes);

    return {
      day,
      to,
      from,
      valid: !closedAllDay,
      openAllDay,
    };
  });
};

export const timeSlotTransform = timeSlots => {
  return timeSlots.map(slot => {
    const closed = slot.openAllDay ? false : !(slot.to && slot.from);
    const openAllDay = slot.openAllDay;
    let fromDate = '';
    let toDate = '';
    let openHour = '';
    let openMinutes = '';
    let closeHour = '';
    let closeMinutes = '';

    if (!closed) {
      fromDate = parse(slot.from, 'HH:mm', new Date());
      toDate = parse(slot.to, 'HH:mm', new Date());
      openHour = getHours(fromDate);
      openMinutes = getMinutes(fromDate);
      closeHour = getHours(toDate);
      closeMinutes = getMinutes(toDate);
    }

    return {
      day_of_week: slot.day,
      closed_all_day: closed,
      open_hour: openHour,
      open_minutes: openMinutes,
      close_hour: closeHour,
      close_minutes: closeMinutes,
      open_all_day: openAllDay,
    };
  });
};

const getLocalizedTimeZoneName = (timeZone, locale) => {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: 'longGeneric',
    })
      .formatToParts(new Date())
      .find(part => part.type === 'timeZoneName')?.value;
  } catch {
    return '';
  }
};

const ZH_TIMEZONE_FALLBACKS = {
  'America/Los_Angeles': '北美太平洋时间',
  'Asia/Shanghai': '中国标准时间',
  'Etc/UTC': '协调世界时',
};

const getLocalizedTimeZoneLabel = (label, timeZone, locale) => {
  if (!locale.toLowerCase().startsWith('zh')) return label;

  const localizedName =
    getLocalizedTimeZoneName(timeZone, 'zh-CN') ||
    ZH_TIMEZONE_FALLBACKS[timeZone];
  const offset = label.match(/\(((?:GMT|UTC)[^)]+)\)$/)?.[1];

  if (!localizedName) return label;
  if (!offset || localizedName.replace('−', '-') === offset.replace('−', '-')) {
    return localizedName;
  }

  return `${localizedName}（${offset}）`;
};

export const timeZoneOptions = (requestedLocale = 'en') => {
  const locale =
    typeof requestedLocale === 'string'
      ? requestedLocale
      : requestedLocale?.value || 'en';

  return Object.keys(timeZoneData).map(key => ({
    label: getLocalizedTimeZoneLabel(key, timeZoneData[key], locale),
    value: timeZoneData[key],
  }));
};
